import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'examfolio.displayLanguageId';

interface DisplayLanguageContextValue {
  /** Selected question-display language id, or null for the master/default language. */
  languageId: string | null;
  setLanguageId: (id: string | null) => void;
}

const DisplayLanguageContext = createContext<DisplayLanguageContextValue | undefined>(undefined);

/**
 * Holds the user's chosen question-display language (persisted in localStorage). This switches
 * the language questions are *shown* in (overlaying the existing translations); it is not full
 * app-UI localization.
 */
export function DisplayLanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [languageId, setLanguageIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  const setLanguageId = useCallback((id: string | null) => {
    setLanguageIdState(id);
    // localStorage can be unavailable (private mode, restricted env) — persistence is best-effort.
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(() => ({ languageId, setLanguageId }), [languageId, setLanguageId]);
  return <DisplayLanguageContext.Provider value={value}>{children}</DisplayLanguageContext.Provider>;
}

export function useDisplayLanguage(): DisplayLanguageContextValue {
  const ctx = useContext(DisplayLanguageContext);
  if (!ctx) throw new Error('useDisplayLanguage must be used within a DisplayLanguageProvider');
  return ctx;
}
